import type { KeyboardEvent } from 'react'
import { ArrowDown, ArrowUp, ChevronDown, ListOrdered, Lock, StarOff, X } from 'lucide-react'
import { validerCheminFavori } from '@gestion/shared/favoris'
import { Link } from '../router.js'
import { useToast } from '../admin/primitives.js'
import {
  allerAncre, composerDeclencheurs, entreesLien, useMenuContextuel, type ActionMenu, type GroupeMenu,
} from './menu-contextuel.js'
import {
  deplacerFavori, etatFavoris, iconeDeFavori, permissionDeFavori, replierFavoris, retirerFavori,
  useFavoris, type Favori,
} from './favoris.js'

/**
 * LA SECTION « FAVORIS » DE LA BARRE LATÉRALE (lot 5, 24/10/2026).
 *
 * En haut de la barre, au-dessus de la navigation : dans le rail comme dans
 * le tiroir mobile (le corps de la barre est rendu deux fois, d'où l'absence
 * d'`id`). Absente tant que la liste est vide.
 *
 * Barre repliée (72 px) : les icônes seules, avec leur nom en infobulle
 * (décision d'Hugo) ; le titre et le bouton de retrait sont masqués, un filet
 * sépare les favoris de la navigation. Le chevron, lui, reste, centré : en
 * mode compact la barre ne se déplie jamais, et c'est lui seul qui rouvre
 * une section repliée.
 *
 * Section repliée (chevron) : la liste n'est pas rendue, dans le rail comme
 * dans le tiroir (l'état est commun) ; la tête garde sa hauteur.
 *
 * Chaque ligne est revérifiée par le validateur partagé AVANT de devenir un
 * lien : une adresse refusée n'est pas rendue (elle se retire depuis
 * Préférences › Mes favoris). Une page devenue interdite d'après le registre
 * est grisée, sans lien, avec « Vous n’y avez plus accès » ; un toucher (ou
 * Entrée) le dit aussi, en toast : sur un téléphone, ni survol ni infobulle.
 * Son bouton de retrait reste. Le bouton de retrait vit À CÔTÉ du lien,
 * jamais dedans : le tiroir se referme sur un clic de lien, pas sur un retrait.
 *
 * Menu d'une ligne : le rang se lit sur les lignes VISIBLES (une adresse
 * refusée n'est pas dans la barre, on ne l'enjambe pas pour rien). « Monter »
 * et « Descendre » restent, grisés en bout de liste ; « Ranger les favoris… »
 * mène au panneau de Préférences.
 *
 * Le nom est rendu en texte, jamais en HTML ; l'infobulle passe par
 * `content: attr(data-tip)`, du texte elle aussi.
 */
/** Une ligne que la barre montre : l'adresse passe le validateur partagé. */
const visible = (f: Favori) => validerCheminFavori(f.path).ok

export function SectionFavoris(props: { adresse: string; peut: (perm?: string) => boolean; sansTete?: boolean }) {
  const { liste, replie: replieSection } = useFavoris()
  // Dans le panneau du menu flottant (`sansTete`), la section EST le panneau :
  // ni titre ni chevron, et jamais repliée — sinon le panneau resterait vide.
  const replie = !props.sansTete && replieSection
  const toast = useToast()
  const menu = useMenuContextuel()
  const lignes = liste.filter(visible)
  if (lignes.length === 0) return null

  // Construit à l'OUVERTURE du menu : le rang se lit dans l'état du moment,
  // sur les lignes que la barre montre.
  const actionsLigne = (f: Favori, accessible: boolean): GroupeMenu[] => {
    const ids = etatFavoris().liste.filter(visible).map((x) => x.id)
    const i = ids.indexOf(f.id)
    const ranger: ActionMenu[] = [
      {
        id: 'favori-monter', libelle: 'Monter', icone: <ArrowUp size={15} />,
        ...(i > 0 ? { agir: () => { void deplacerFavori(f.id, -1, toast, visible) } } : { indisponible: 'Déjà en tête' }),
      },
      {
        id: 'favori-descendre', libelle: 'Descendre', icone: <ArrowDown size={15} />,
        ...(i >= 0 && i < ids.length - 1 ? { agir: () => { void deplacerFavori(f.id, 1, toast, visible) } } : { indisponible: 'Déjà en dernier' }),
      },
      { id: 'favori-ranger', libelle: 'Ranger les favoris…', icone: <ListOrdered size={15} />, agir: () => allerAncre('/settings/appearance', 'favoris') },
    ]
    return [
      ...(accessible ? [{ actions: entreesLien(f.path, toast) }] : []),
      { actions: ranger },
      { actions: [{ id: 'favori-retirer', libelle: 'Retirer des favoris', icone: <StarOff size={15} />, agir: () => { void retirerFavori(f, toast) } }] },
    ]
  }

  // Un toucher sur une ligne grisée : la raison, comme l'étoile indisponible.
  const plusAcces = () => toast('error', 'Vous n’y avez plus accès.')
  const entreePlusAcces = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); plusAcces() }
  }

  return (
    <nav className={`lc-nav lc-favs${replie ? ' lc-favs--replie' : ''}`} aria-label="Favoris">
      {props.sansTete ? null : (
        <div className="lc-favs__tete">
          <span className="lc-favs__titre">Favoris</span>
          <button
            type="button"
            className="lc-navfam__chev lc-favs__chev"
            aria-label={replie ? 'Déplier les favoris' : 'Replier les favoris'}
            aria-expanded={!replie}
            onClick={() => replierFavoris(!replie)}
          >
            <ChevronDown size={14} aria-hidden="true" />
          </button>
        </div>
      )}
      {/* Section repliée : la liste n'est pas rendue du tout (rail et tiroir
          ensemble) ; la tête reste, à la même hauteur. */}
      {replie ? null : (
        <div className="lc-favs__liste">
          {lignes.map((f) => {
            const Icone = iconeDeFavori(f.path)
            const accessible = props.peut(permissionDeFavori(f.path))
            return (
              <div key={f.id} className={`lc-navfam__row lc-fav${accessible ? '' : ' lc-fav--interdit'}`}>
                {accessible ? (
                  <Link
                    href={f.path}
                    className="lc-nav__item lc-fav__lien"
                    data-tip={f.label}
                    aria-current={f.path === props.adresse ? 'page' : undefined}
                    {...menu(() => actionsLigne(f, true), f.label)}
                  >
                    <Icone size={18} strokeWidth={2} className="lc-nav__icon" />
                    <span className="lc-nav__label">{f.label}</span>
                  </Link>
                ) : (
                  <span
                    className="lc-nav__item lc-fav__lien"
                    role="link"
                    aria-disabled="true"
                    tabIndex={0}
                    aria-label={`${f.label} — vous n’y avez plus accès`}
                    data-tip={`${f.label} — vous n’y avez plus accès`}
                    title="Vous n’y avez plus accès"
                    onClick={plusAcces}
                    {...composerDeclencheurs(menu(() => actionsLigne(f, false), f.label), { onKeyDown: entreePlusAcces })}
                  >
                    <Icone size={18} strokeWidth={2} className="lc-nav__icon" />
                    <span className="lc-nav__label">{f.label}</span>
                    <Lock size={13} className="lc-fav__cadenas" aria-hidden="true" />
                  </span>
                )}
                <button
                  type="button"
                  className="lc-fav__retirer"
                  aria-label={`Retirer « ${f.label} » des favoris`}
                  title="Retirer des favoris"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); void retirerFavori(f, toast) }}
                >
                  <X size={14} aria-hidden="true" />
                </button>
              </div>
            )
          })}
        </div>
      )}
    </nav>
  )
}
